/**
 * Phase 134C — Studio Coachmark
 * --------------------------------------------------------------------
 * Lightweight dismissible tooltip pointing at the "Go Live" button on
 * /livex/studio. Renders ONLY for academies that have not yet streamed
 * (determined upstream via /api/livex/me/activation-status). Dismissed
 * via localStorage key `livex_studio_coachmark_dismissed_{academy_id}`.
 *
 * Implementation: positioned absolutely relative to a parent wrapper, so
 * the parent should be `position: relative`. The coachmark is anchored
 * to the bottom-right of its wrapper by default; the parent can override
 * with custom positioning if needed.
 *
 * Strategic intent: reduce hesitation without creating learning fatigue.
 * Auto-removes after activation (the gate disappears once first_stream
 * exists). Never appears once dismissed.
 */
import { useState } from 'react';
import { Sparkles, X } from 'lucide-react';
import '@/styles/livex.css';

export default function LivexStudioCoachmark({ academyId, label, message }) {
  // Skip rendering if we can't tie dismissal to a specific academy.
  const storageKey = academyId ? `livex_studio_coachmark_dismissed_${academyId}` : 'livex_studio_coachmark_dismissed_anon';
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(storageKey) === '1'; } catch { return false; }
  });
  if (!academyId) return null;
  if (dismissed) return null;
  const handleDismiss = () => {
    try { localStorage.setItem(storageKey, '1'); } catch { /* ignore */ }
    setDismissed(true);
  };
  return (
    <div
      data-testid="livex-studio-coachmark"
      role="note"
      aria-label="Activation hint"
      className="absolute left-1/2 -translate-x-1/2 -bottom-3 translate-y-full z-30 max-w-xs livex-burst"
    >
      <div
        className="relative px-4 py-3 rounded-xl border-2 shadow-2xl"
        style={{
          background: 'linear-gradient(135deg, #1a0e10, #07111F)',
          borderColor: '#FF5A1F',
          boxShadow: '0 12px 32px rgba(255,90,31,0.45)',
        }}
      >
        {/* Pointer arrow */}
        <div
          aria-hidden
          className="absolute -top-2 left-1/2 -translate-x-1/2 w-3 h-3 rotate-45 border-l-2 border-t-2"
          style={{ background: '#1a0e10', borderColor: '#FF5A1F' }}
        />
        <button
          onClick={handleDismiss}
          aria-label="Dismiss coachmark"
          data-testid="livex-studio-coachmark-close"
          className="absolute top-1.5 right-1.5 inline-flex items-center justify-center w-6 h-6 text-[#94A3B8] hover:text-white rounded transition-colors"
        >
          <X className="h-3 w-3" />
        </button>
        <div className="flex items-start gap-2 pr-5">
          <Sparkles className="h-4 w-4 text-[#FF5A1F] flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-[11px] font-black uppercase tracking-[0.15em] text-[#FF5A1F]">{label || 'First time?'}</div>
            <div className="text-[13px] font-bold text-white leading-snug mt-0.5">
              {message || 'Tap here to start your first academy broadcast.'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
